import { memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Actions } from "flexlayout-react";
import { styled } from "@mui/material/styles";
import MuiDivider from "@mui/material/Divider";
import SoftTypography from "components/SoftTypography";
import CustomDrawerPanel from "layouts/Flow Designer/components/CustomDrawerPanel";
import SoftBox from "components/SoftBox";
import { TRIGGER_TYPES } from "constants/flowDesigner";
import HttpEndpointTriggerForm from "./components/HttpEndpointTriggerForm";
import ScheduleTriggerForm from "./components/ScheduleTriggerForm";
import McpTriggerForm from "./components/McpTriggerForm";
import { useDetermineShareFlowUserAccessType } from "hooks/useDetermineShareFlowUserAccessType";
import { SocketIoTriggerForm } from "./components/SocketIoTriggerForm";
import useAllowEditWorkflow from "hooks/useAllowEditWorkflow";
import { ThemedAngleLeft } from "assets/CXFIcons/Arrows";
import { showStartNodePanel } from "redux/actions/flowDesigner/componentPropsPanelActionCreator";
import { closeTriggerPropertiesPanel } from "redux/actions/flowDesigner/triggersActionCreator";
import { useFlexLayout } from "context/AppProvider/FlexLayoutContext";
import { FLEX_LAYOUT_COMPONENT } from "constants/flowDesigner";
import { setSelectedTrigger } from "redux/actions/flowDesigner/triggersActionCreator";

const Divider = styled(MuiDivider)(() => ({
  marginBottom: 0
}));

const ComponentPropsWrapper = styled(SoftBox)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 4px",
  width: "100%"
}));

const TriggerPropertiesPanel = ({ trigger }) => {
  const { showTriggerPropertiesPanel, selectedSchedule, selectedTrigger: selectedTriggerFromStore } = useSelector(
    (store) => store?.designer?.triggers
  );
  const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
  const { isReadOnlyAccess } = useDetermineShareFlowUserAccessType(currentSavedFlow?.id);
  const { allowEditWorkflow } = useAllowEditWorkflow();
  const dispatch = useDispatch();
  const { model } = useFlexLayout();

  const selectedTrigger = trigger || selectedTriggerFromStore;

  const renderFormBody = () => {
    switch (selectedTrigger?.type) {
      case TRIGGER_TYPES.WEBHOOK:
      case TRIGGER_TYPES.REST_ENDPOINT:
      case TRIGGER_TYPES.REDIS:
        return (
          <HttpEndpointTriggerForm
            selectedTrigger={selectedTrigger}
            showTriggerPropertiesPanel={showTriggerPropertiesPanel}
            allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)}
          />
        );
      case TRIGGER_TYPES.SOCKET_IO:
        return (
          <SocketIoTriggerForm
            allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)}
            handleGoBackToStartNodePanel={handleGoBackToStartNodePanel}
          />
        );
      case TRIGGER_TYPES.SCHEDULE:
        return (
          <ScheduleTriggerForm
            allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)}
            selectedTrigger={selectedTrigger}
            handleGoBackToStartNodePanel={handleGoBackToStartNodePanel}
          />
        );
      case TRIGGER_TYPES.MCP:
        return (
          <McpTriggerForm
            selectedTrigger={selectedTrigger}
            allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)}
            handleGoBackToStartNodePanel={handleGoBackToStartNodePanel}
          />
        );
      default:
        return null;
    }
  };

  const handleGoBackToStartNodePanel = (event) => {
    if (event && event.stopPropagation) event.stopPropagation();
    const tabId = "startNodeProps";
    model.doAction(
      Actions.updateNodeAttributes(tabId, {
        component: FLEX_LAYOUT_COMPONENT.startNode,
        config: {}
      })
    );
    model.doAction(Actions.updateNodeAttributes(tabId, { name: "Start Node" }));
    model.doAction(Actions.selectTab(tabId));
    dispatch(setSelectedTrigger(null));
    dispatch(closeTriggerPropertiesPanel());
    dispatch(showStartNodePanel());
  };

  return (
    <CustomDrawerPanel
      open={showTriggerPropertiesPanel}
      className="no-translate trigger-props-panel"
    >
      <ComponentPropsWrapper className="panel-container">
        <SoftBox
          className="panel-database-sibling"
          display="flex"
          alignItems="center"
          sx={{ cursor: "pointer" }}
          onClick={handleGoBackToStartNodePanel}
        >
          <ThemedAngleLeft
            style={{ transform: "rotate(180deg)", marginRight: 4 }}
            fontSize="small"
          />
          <SoftTypography variant="h6" fontWeight="bold">
            {selectedSchedule ? selectedSchedule?.title : selectedTrigger?.label}
          </SoftTypography>
        </SoftBox>
      </ComponentPropsWrapper>
      <SoftBox padding="0 4px">
        <Divider className="component-props-divider" />
      </SoftBox>
      {renderFormBody()}
    </CustomDrawerPanel>
  );
};

export default memo(TriggerPropertiesPanel);
